/**
 * Texas EVV by visit (R-177): mirrors the backend rule in shared-services patientmr/evvRules.js,
 * so the pre-visit HHAX prompt matches what the server records. The server decision is final.
 *
 * Payer: Medicaid other than CHIP (insuranceSummary.evvRequired). Service:
 *   REQUIRED      PT, OT, home health aide, skilled nursing visits (claims need a matching EVV visit)
 *   OPTIONAL      Private Duty Nursing (collected only when the agency opts in)
 *   NOT_REQUIRED  speech therapy, MSW
 */

import { disciplineForProviderType, paCategoryService, serviceEvvClass, serviceForDiscipline } from '../constants/disciplines';

export type EvvClass = 'REQUIRED' | 'OPTIONAL' | 'NOT_REQUIRED';
export type NursingServiceType = 'PDN' | 'SKILLED_NURSING_VISIT';

// Phase 20A: provider types, disciplines and EVV classes from the discipline registry

export function isNursingDiscipline(discipline?: string | null): boolean {
  return disciplineForProviderType(discipline) === 'RN';
}

export interface VisitService {
  service: string;
  evvClass: EvvClass;
}

const classOf = (service: string): EvvClass => serviceEvvClass(service) || 'REQUIRED';

/** RN/LVN: chosen visit type, then the PA's service category, then PDN. */
export function classifyVisitService(input: {
  discipline?: string | null;
  serviceType?: NursingServiceType | '' | null;
  paServiceCategory?: string | null;
}): VisitService {
  const disc = String(input.discipline || '').toUpperCase();
  const category = String(input.paServiceCategory || '').toLowerCase();
  const registryDiscipline = disciplineForProviderType(disc);
  if (registryDiscipline === 'RN') {
    const service = input.serviceType
      ? (input.serviceType === 'PDN' ? 'PDN' : 'SKILLED_NURSING_VISIT')
      : category ? (category === 'pdn' ? 'PDN' : 'SKILLED_NURSING_VISIT') : 'PDN';
    return { service, evvClass: classOf(service) };
  }
  const service = registryDiscipline ? serviceForDiscipline(registryDiscipline) : null;
  if (service) return { service, evvClass: classOf(service) };
  const fromPa = paCategoryService(category);
  if (fromPa) return { service: fromPa, evvClass: classOf(fromPa) };
  return { service: disc || 'UNKNOWN', evvClass: 'REQUIRED' };
}

export interface VisitEvvDecision {
  /** Claims need a matching EVV visit */
  evvRequired: boolean;
  /** Prompt the clinician to clock in/out on HHAX */
  evvCollect: boolean;
}

export function decideVisitEvv(input: { payerEvv: boolean; evvClass: EvvClass; collectPdnVisits?: boolean }): VisitEvvDecision {
  if (!input.payerEvv) return { evvRequired: false, evvCollect: false };
  if (input.evvClass === 'REQUIRED') return { evvRequired: true, evvCollect: true };
  if (input.evvClass === 'OPTIONAL') return { evvRequired: false, evvCollect: input.collectPdnVisits === true };
  return { evvRequired: false, evvCollect: false };
}
